/**
 *
 * enrichSet
 * Recursively convert arrays to idObjects
 * Except for timeslots, used too often as array
 * Add info to slots and meetings
 *
 */

import { sortBy, get } from 'lodash-es';
import produce from 'immer';
import { idValues, idMap } from 'utils/arrays.js';
import setIn from 'utils/setIn.js';
import { eventLimits } from 'utils/eventTimes.js';
import notReady from 'utils/notReady.js';
import { addMinutes, subMinutes, isPast, isFuture } from 'utils/date-fns/index.js';
import {
  SLOT_OPEN, SLOT_ASSIGNED, SLOT_BREAK,
  MEETING_STATUS_ASSIGNED, MEETING_STATUS_CLOSED, MEETING_STATUS_OPEN, MEETING_STATUS_WAITING,
} from '../constants.js';

function enrichSet(meetingSet) {
  if (notReady(meetingSet)) {
    return meetingSet;
  }

  function enrichSlot(slot) {
    if (!slot.meetingId || !meetings) {
      return slot;
    }
    const meeting = meetings[slot.meetingId];
    return meeting ? { ...slot, meeting } : { ...slot, meetingId: null, status: SLOT_OPEN };
  }

  function enrichBlock(block) {
    const { timeslots } = block;
    const { start, end } = eventLimits(timeslots);
    const availableSlots = timeslots.filter((s) => s.status !== SLOT_BREAK).length;
    const openSlots = timeslots.filter((s) => s.status === SLOT_OPEN).length;
    return {
      ...block,
      start,
      end: addMinutes(end, meetingSet.inBetween),
      timeslots: timeslots.map(enrichSlot),
      availableSlots,
      openSlots,
    };
  }

  function enrichMeeting(meeting) {
    const { id, studentId, parentIds } = meeting;
    const student = meetingSet.students[studentId];
    const slots = sortBy(slotsByMeeting[id], 'start');
    const details = {
      assigned: Boolean(slots && slots.length > 0),
      slots,
      set: meetingSet,
      student,
      parents: student.connections ? parentIds.map((pid) => student.connections[pid]).filter(Boolean) : null,
    };
    const result = {
      ...meeting,
      details,
    };
    const info = get(meeting, 'videoMeeting.info');
    if (!info || !info.id || info.eventIds) {
      return result;
    }
    const eventIds = slots.reduce((acc, slot) => ({ [slot.id]: info.id }));
    return setIn(result, 'videoMeeting.info.eventIds', eventIds);
  }

  // Get slots for each meeting
  const slotsByMeeting = {};

  const timeslots = meetingSet.blocks.list && sortBy(meetingSet.blocks.list.flatMap((id) => meetingSet.blocks[id].timeslots), 'start');
  if (timeslots) {
    timeslots.forEach((slot) => {
      if (slot.meetingId) {
        slotsByMeeting[slot.meetingId] = [...(slotsByMeeting[slot.meetingId] || []), slot];
      }
    });
  }
  // make IdObject with meetings with details
  const meetings = meetingSet.meetings.list && idMap(meetingSet.meetings, enrichMeeting);

  if (meetings) {
    meetings.list = sortBy(meetings.list, ((mtId) => meetings[mtId].details.student.displayName));
  }

  // Make idObject from filtered blocks, add meeting to slots
  const blocks = meetingSet.blocks.list && idMap(meetingSet.blocks, enrichBlock);
  if (blocks) {
    blocks.list = sortBy(blocks.list, (id) => blocks[id].start);
  }
  const meetingsPlanned = meetings && meetings.list.filter((id) => Boolean(slotsByMeeting[id])).length;
  const { start, end } = blocks ? eventLimits(idValues(blocks)) : {};
  const timeStep = meetingSet.duration + meetingSet.inBetween;
  const registerClose = meetingSet.registerEnd || subMinutes(end, timeStep);

  function getStatus() {
    if (isPast(end)) {
      return MEETING_STATUS_CLOSED;
    }
    if (timeslots && timeslots.some((o) => o.status === SLOT_ASSIGNED)) {
      return MEETING_STATUS_ASSIGNED;
    }
    if (isFuture(start)) {
      return MEETING_STATUS_WAITING;
    }
    return isFuture(registerClose) ? MEETING_STATUS_OPEN : MEETING_STATUS_CLOSED;
  }

  const status = getStatus();
  const meetings2 = produce(meetings, (draft) => {
    meetings.list.forEach((id) => {
      draft[id].details.set.registerClose = registerClose;
      draft[id].details.set.setStatus = status;
    });
  });
  const result = {
    ...meetingSet,
    start,
    end,
    meetingsPlanned,
    meetings: meetings2,
    blocks,
    timeStep,
    timeslots,
    availableSlots: timeslots.filter((s) => s.status !== SLOT_BREAK).length,
    registerClose,
    status,
  };
  return result;
}

export default enrichSet;
